import { connect } from 'react-redux'
import styled from 'styled-components'
import cn from 'classnames'

import useHasHydrated from 'hooks/useHasHydrated'
import useSearchParams from 'hooks/useSearchParams'

import { slugToNice } from '../../components/utils/stringUtils'
import { formatPrice } from '../../components/utils/pricing'

import Container from '../../components/layout/Container'
import LazyImage from '../../components/display/LazyImage'
import Button from '../../components/display/Button'
import InstantBookBadge from '../../components/display/InstantBookBadge'
import InlineSpinner from '../../components/display/InlineSpinner'

import GiftIcon from '../../components/icons/Gift'

const Wrapper = styled.div`
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;

  display: flex;
  z-index: 1;

  opacity: 0;
  transition: opacity 300ms ease-out;
  background-color: white;

  ${({ visible }) =>
    visible &&
    `
    opacity: 1;
  `}
`

const Inner = styled.div`
  display: flex;
  align-items: center;

  padding: 24px 0;
  border-top: 1px solid #d1d1d1;
`

const Content = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`

const Title = styled.h1`
  font-size: 20px;
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: 0.25px;
`

const Price = styled.div`
  font-size: 20px;
  font-weight: 500;
  line-height: 1.1;
  letter-spacing: 0.25px;
  color: ${({ theme }) => theme.brand.forest};
`

const ImageWrapper = styled.figure`
  width: 120px;
  height: 80px;

  padding: 0;
  margin: 0;
  margin-right: 24px;

  background-color: #333;
`

const IconButton = styled(Button)`
  padding: 12px;

  .icon {
    font-size: 18px;
    margin: 0;
  }
`

const Actions = styled.div`
  display: flex;

  ${Button} {
    padding: 12px 16px;
  }

  ${Button}, ${IconButton} {
    margin-right: 8px;

    &:last-child {
      margin-right: 0;
    }

    svg {
      width: 18px;
      margin: 0;
    }
  }
`

const DesktopBookingBar = ({
  listing,
  storyId,
  promoImage,
  enquiry,
  onBook,
  myBucketlist,
  isInBucketList,
  onBucketListButtonClick,
  visible,
}) => {
  const hasTotal = enquiry.data && enquiry.data.total > 0

  const searchParams = useSearchParams()

  const hasHydrated = useHasHydrated()

  const hasDatesInQueryString =
    hasHydrated &&
    searchParams?.has('startDate') &&
    searchParams?.has('endDate')

  const hasDatesInEnquiry =
    enquiry?.data?.start_date != null && enquiry?.data?.end_date

  const bookCtaLabel =
    hasDatesInQueryString || hasDatesInEnquiry
      ? 'Book now'
      : 'Check availability'

  return (
    <Wrapper visible={visible} className="desktop-booking-bar">
      <Container>
        <Inner>
          <Content>
            <ImageWrapper>
              <LazyImage data-src={promoImage} preload />
            </ImageWrapper>
            <div>
              <Title>
                <span>{listing.title}</span>
              </Title>
              <Price>
                <span>
                  {hasTotal
                    ? formatPrice(enquiry.data.total, 0)
                    : formatPrice(listing.price, 0)}
                </span>{' '}
                <span>
                  {hasTotal ? 'total' : slugToNice(listing.pricing_suffix)}
                </span>
                {listing.instant_book_enabled && (
                  <span
                    style={{ position: 'relative', top: -4, marginLeft: 16 }}
                  >
                    <InstantBookBadge />
                  </span>
                )}
              </Price>
            </div>
          </Content>
          <Actions>
            <Button onClick={onBook} uppercase={false}>
              {bookCtaLabel}
            </Button>
            <IconButton
              onClick={onBucketListButtonClick}
              aria-label={
                isInBucketList
                  ? 'Remove from bucket list'
                  : 'Add to bucket list'
              }
              ghost
            >
              {!myBucketlist.fetching && (
                <i
                  className={cn('icon', {
                    'icon-bucketlist-heart-off': !isInBucketList,
                    'icon-bucketlist-heart-on': isInBucketList,
                  })}
                />
              )}
              {myBucketlist.fetching && <InlineSpinner />}
            </IconButton>
            {listing.giftable && (
              <IconButton
                as="a"
                href={`/vouchers/create?story_id=${storyId}`}
                ghost
              >
                <GiftIcon />
              </IconButton>
            )}
          </Actions>
        </Inner>
      </Container>
    </Wrapper>
  )
}

export default connect(({ myBucketlist, enquiry }) => ({
  myBucketlist,
  enquiry,
}))(DesktopBookingBar)
